import { useState, useEffect, useRef, useCallback } from "react";
import Filters from "./Components/Filters/Filters";
import HomeAdSection from "./Components/HomeAdSection/HomeAdSection";
import HomeBrowse from "./Components/HomeBrowse/HomeBrowse";
import Minigame from "./Components/Minigame/Minigame";
import MonthlyWoman from "./Components/MonthlyWoman/MonthlyWoman";
import NewsSection from "./Components/NewsSection/NewsSection";
import RemarkableWomenSection from "./Components/RemarkableWomenSection/RemarkableWomenSection";
import Title from "./Components/Title/Title";
import WomenResultsGrid from "./Components/WomenResultsGrid/WomenResultsGrid";
import { useHomeData } from "../../context/HomeContext";
import API from "../../config/api";
import { AD_SLOTS } from "../../config/adSlots";
import { useTranslation } from "../../i18n/LanguageProvider";
import "./Home.sass";

const ITEMS_PER_PAGE = 8;

function Home() {
    const { topWomen, defaultWomen, monthlyWoman, news, directory, initialGameData } = useHomeData();
    const { language, t } = useTranslation();
    const [filteredWomen, setFilteredWomen] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [currentFilters, setCurrentFilters] = useState(null);
    const loadedIdsRef = useRef([]);
    const offsetRef = useRef(0);
    const loaderRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Load initial data
    useEffect(() => {
        if (defaultWomen && !isFiltered) {
            // defaultWomen comes from context, handle both old and new format
            if (Array.isArray(defaultWomen)) {
                setFilteredWomen(defaultWomen);
                setHasMore(defaultWomen.length >= ITEMS_PER_PAGE);
                loadedIdsRef.current = defaultWomen.map((w) => w.id);
            } else if (defaultWomen.data) {
                setFilteredWomen(defaultWomen.data);
                setHasMore(defaultWomen.hasMore);
                loadedIdsRef.current = defaultWomen.data.map((w) => w.id);
            }
        }
    }, [defaultWomen, isFiltered]);

    const loadMore = useCallback(() => {
        if (loading || !hasMore) return;

        setLoading(true);
        const params = new URLSearchParams();

        if (isFiltered && currentFilters) {
            params.append("filteredWomen", "true");
            if (currentFilters.search) params.append("search", currentFilters.search);
            if (currentFilters.categoryIds?.length > 0) {
                params.append("category_ids", currentFilters.categoryIds.join(","));
            }
            if (currentFilters.placeIds?.length > 0) {
                params.append("place_ids", currentFilters.placeIds.join(","));
            }
            params.append("limit", ITEMS_PER_PAGE);
            params.append("offset", offsetRef.current);
        } else {
            params.append("isDefault", "true");
            params.append("limit", ITEMS_PER_PAGE);
            if (loadedIdsRef.current.length > 0) {
                params.append("exclude_ids", loadedIdsRef.current.join(","));
            }
        }
        params.append("lang", language);

        fetch(`${API.getWoman}?${params.toString()}`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                const newWomen = data.data || [];
                setFilteredWomen((prev) => [...prev, ...newWomen]);
                setHasMore(data.hasMore);
                if (isFiltered) {
                    offsetRef.current += newWomen.length;
                } else {
                    loadedIdsRef.current = [...loadedIdsRef.current, ...newWomen.map((w) => w.id)];
                }
            })
            .catch((err) => console.error("Error loading more women:", err))
            .finally(() => setLoading(false));
    }, [loading, hasMore, isFiltered, currentFilters, language]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    loadMore();
                }
            },
            { threshold: 0.1 },
        );

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, loadMore]);

    const handleFiltersChange = (filters) => {
        if (!filters) {
            // Reset to default (random)
            setIsFiltered(false);
            setCurrentFilters(null);
            loadedIdsRef.current = [];
            offsetRef.current = 0;
            setFilteredWomen([]);
            setHasMore(true);
            setLoading(true);

            fetch(`${API.getWoman}?isDefault=true&limit=${ITEMS_PER_PAGE}&lang=${language}`, {
                credentials: "include",
            })
                .then((res) => res.json())
                .then((data) => {
                    setFilteredWomen(data.data || []);
                    setHasMore(data.hasMore);
                    loadedIdsRef.current = (data.data || []).map((w) => w.id);
                })
                .catch((err) => console.error("Error fetching default women:", err))
                .finally(() => setLoading(false));
            return;
        }

        setIsFiltered(true);
        setCurrentFilters(filters);
        offsetRef.current = 0;
        loadedIdsRef.current = [];
        setFilteredWomen([]);
        setHasMore(true);
        setLoading(true);

        const params = new URLSearchParams({ filteredWomen: "true" });

        if (filters.search) params.append("search", filters.search);
        if (filters.categoryIds.length > 0) {
            params.append("category_ids", filters.categoryIds.join(","));
        }
        if (filters.placeIds.length > 0) {
            params.append("place_ids", filters.placeIds.join(","));
        }
        params.append("limit", ITEMS_PER_PAGE);
        params.append("offset", 0);
        params.append("lang", language);

        fetch(`${API.getWoman}?${params.toString()}`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                setFilteredWomen(data.data || []);
                setHasMore(data.hasMore);
                offsetRef.current = (data.data || []).length;
                setTimeout(() => {
                    document.getElementById("filtered-results")?.scrollIntoView({ behavior: "smooth" });
                }, 100);
            })
            .catch((err) => console.error("Error fetching filtered women:", err))
            .finally(() => setLoading(false));
    };

    return (
        <div className="general-wrapper">
            <RemarkableWomenSection women={topWomen || []} />
            {(topWomen || []).length > 0 && <HomeAdSection adSlot={AD_SLOTS.displayInline} className="home__ad" />}
            <MonthlyWoman woman={monthlyWoman} />
            {monthlyWoman && <HomeAdSection adSlot={AD_SLOTS.displayInline} className="home__ad" />}
            <NewsSection news={news} />
            {news?.length > 0 && <HomeAdSection adSlot={AD_SLOTS.displayInline} className="home__ad" />}
            <HomeBrowse directory={directory} />
            <Title text={t("home.recognize")} />
            <Minigame initialGameData={initialGameData} />
            <HomeAdSection adSlot={AD_SLOTS.displayInline} className="home__ad" />

            <section id="women-directory" className="home-directory">
                <Title text={t("home.successful")} />
                <Filters onFiltersChange={handleFiltersChange} />
                <div id="filtered-results" className="cards home-results">
                    <WomenResultsGrid women={filteredWomen} isFiltered={isFiltered} noResultsText={t("home.noResults")} />
                </div>
            </section>

            {/* Infinite scroll loader */}
            {hasMore && (
                <div ref={loaderRef} className="scroll-loader">
                    {loading && <p>{t("home.loading")}</p>}
                </div>
            )}
        </div>
    );
}

export default Home;
